// Left column form: file upload + domain + document type + mode + submit
// Extracted from SyntheseMarchesPage.js
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  FileText,
  Upload,
  Loader2,
  CheckCircle2,
  Sparkles,
  Hammer,
  Package,
  Briefcase,
  FileCheck,
  Zap,
} from 'lucide-react';

const DOMAINS = [
  { id: 'travaux', label: 'Travaux', icon: Hammer, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { id: 'fournitures', label: 'Fournitures', icon: Package, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'services', label: 'Services', icon: Briefcase, color: 'bg-green-100 text-green-700 border-green-200' },
];

const DOCUMENT_TYPES = [
  { id: 'rc', label: 'Règlement de Consultation', shortLabel: 'RC' },
  { id: 'ccap', label: 'CCAP', shortLabel: 'CCAP' },
  { id: 'cctp', label: 'CCTP', shortLabel: 'CCTP' },
];

const MODES = [
  { id: 'courte', label: 'Courte' },
  { id: 'standard', label: 'Standard' },
  { id: 'detaillee', label: 'Détaillée' },
];

export default function UploadForm({
  fileInputRef,
  fileData,
  fileName,
  domain,
  setDomain,
  documentType,
  setDocumentType,
  mode,
  setMode,
  customPrompt,
  setCustomPrompt,
  loading,
  onFileChange,
  onDrop,
  onSubmit,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="space-y-6"
    >
      {/* File Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-600" />
            Document PDF
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            data-testid="upload-dropzone"
            className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
              ${fileData
                ? 'border-green-300 bg-green-50'
                : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/50'
              }
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={onFileChange}
              className="hidden"
              data-testid="file-input"
            />
            {fileData ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
                <p className="font-medium text-green-700">{fileName}</p>
                <p className="text-sm text-green-600">Document prêt pour l'analyse</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-10 h-10 text-slate-400" />
                <p className="font-medium text-slate-700">Glissez votre PDF ici</p>
                <p className="text-sm text-slate-500">ou cliquez pour parcourir</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Domain Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">1. Domaine du marché</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {DOMAINS.map((d) => (
              <button
                key={d.id}
                onClick={() => setDomain(d.id)}
                data-testid={`domain-${d.id}`}
                className={`
                  p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2
                  ${domain === d.id ? `${d.color} border-current` : 'border-slate-200 hover:border-slate-300 bg-white'}
                `}
              >
                <d.icon className="w-6 h-6" />
                <span className="font-medium text-sm">{d.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Document Type */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">2. Type de document</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {DOCUMENT_TYPES.map((dt) => (
              <button
                key={dt.id}
                onClick={() => setDocumentType(dt.id)}
                data-testid={`doctype-${dt.id}`}
                className={`
                  w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 text-left
                  ${documentType === dt.id ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300 bg-white'}
                `}
              >
                <FileCheck className={`w-5 h-5 ${documentType === dt.id ? 'text-violet-600' : 'text-slate-400'}`} />
                <div>
                  <span className={`font-medium ${documentType === dt.id ? 'text-violet-700' : 'text-slate-700'}`}>
                    {dt.label}
                  </span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {dt.shortLabel}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Synthesis Mode */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            3. Niveau de synthèse
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                data-testid={`mode-${m.id}`}
                className={`
                  flex-1 py-3 px-4 rounded-xl font-medium transition-all
                  ${mode === m.id ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}
                `}
              >
                {m.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Prompt */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Instructions supplémentaires (optionnel)</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Ex: Focus sur les délais et pénalités..."
            data-testid="custom-prompt-input"
            className="w-full p-4 border border-slate-200 rounded-xl resize-none h-24 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
        </CardContent>
      </Card>

      {/* Submit Button */}
      <Button
        onClick={onSubmit}
        disabled={loading || !fileData || !domain || !documentType}
        data-testid="analyse-submit-btn"
        className="w-full py-6 text-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Analyse en cours...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            Analyser le document
          </>
        )}
      </Button>
    </motion.div>
  );
}
